import { hasScope } from '@mesa/core/auth/utils';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { ErrorState } from '@/components/error-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Skeleton } from '@/components/ui/skeleton';
import { WebhookEditDialog } from '@/components/webhook-edit-dialog';
import { pushError } from '@/lib/faro';
import { orgBySlugQueryOptions } from '@/lib/org-query-options';
import { webhookQueryOptions } from '@/lib/webhook-query-options';
import { deleteWebhookTargetServerFn, regenerateWebhookSecretServerFn } from '@/server/functions/webhook';

function WebhookDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center">
        <div className="h-8 w-8 -ml-1 flex items-center justify-center text-mesa-muted">
          <Icon name="angle-left" className="h-4 w-4" />
        </div>
        <Skeleton className="h-6 w-64 rounded" />
        <div className="shrink-0 flex items-center gap-2">
          <Skeleton className="h-9 w-28 rounded" />
          <Skeleton className="h-9 w-24 rounded" />
        </div>
      </div>

      <section className="border border-mesa-border p-6 space-y-4">
        <Skeleton className="h-4 w-32 rounded" />
        <dl className="grid grid-cols-[160px_1fr] gap-y-3 gap-x-4 text-sm">
          {Array.from({ length: 7 }).map((_, i) => (
            <Fragment key={i}>
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-4 rounded" style={{ width: `${[60, 70, 50, 75, 40, 55, 45][i]}%` }} />
            </Fragment>
          ))}
        </dl>
      </section>

      <section className="border border-mesa-border p-6 space-y-4">
        <div className="space-y-1">
          <Skeleton className="h-4 w-32 rounded" />
          <Skeleton className="h-3 w-72 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="flex-1 h-9 rounded" />
          <Skeleton className="h-9 w-16 rounded" />
          <Skeleton className="h-9 w-20 rounded" />
        </div>
      </section>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/webhooks_/$id')({
  ssr: 'data-only',
  loader: async ({ context: { queryClient, org }, params }) => {
    await queryClient.ensureQueryData(webhookQueryOptions(org.slug, params.id));
  },
  pendingComponent: WebhookDetailSkeleton,
  errorComponent: ErrorState,
  component: WebhookDetailPage,
});

function formatDate(value: Date | null): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
}

function WebhookDetailPage() {
  const { org: routeOrg } = Route.useRouteContext();
  const { orgSlug, id } = Route.useParams();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;
  const canManage = hasScope(org.scopes, 'admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: webhook } = useSuspenseQuery(webhookQueryOptions(org.slug, id));

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => deleteWebhookTargetServerFn({ data: { orgSlug: org.slug, webhookTargetId: webhook.id } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['webhook-list', org.slug] });
      navigate({ to: '/$orgSlug/webhooks', params: { orgSlug: org.slug } });
    },
    onError: (err) => {
      pushError(err);
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateWebhookSecretServerFn({ data: { orgSlug: org.slug, webhookTargetId: webhook.id } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['webhook', org.slug, webhook.id] });
      setIsRegenerateOpen(false);
      setSecretRevealed(true);
    },
    onError: (err) => {
      pushError(err);
    },
  });

  const title = webhook.name ?? webhook.url;
  const showUrlSubtitle = webhook.name !== null && webhook.name !== '';

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  const handleCopySecret = async () => {
    await navigator.clipboard.writeText(webhook.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center">
        <Link
          to="/$orgSlug/webhooks"
          params={{ orgSlug: org.slug }}
          className="text-mesa-muted hover:text-mesa-fg flex items-center justify-center h-8 w-8 -ml-1"
          aria-label="Back to webhooks"
        >
          <Icon name="angle-left" className="h-4 w-4" />
        </Link>
        <h1 className="text-xl font-semibold truncate">{title}</h1>
        {canManage ? (
          <div className="shrink-0 flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsEditOpen(true)}>
              <Icon name="pen" className="h-4 w-4 mr-1" />
              Edit target
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeleteOpen(true)}
              className="text-mesa-red hover:text-mesa-red"
            >
              <Icon name="trash" className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        ) : (
          <div />
        )}
        {showUrlSubtitle && (
          <>
            <div />
            <div className="flex items-center gap-2 text-xs text-mesa-muted min-w-0">
              <code className="font-mono truncate">{webhook.url}</code>
              <button
                type="button"
                onClick={() => handleCopy(webhook.url)}
                className="hover:text-mesa-fg shrink-0"
                aria-label="Copy URL"
              >
                <Icon name="copy" className="h-3.5 w-3.5" />
              </button>
            </div>
            <div />
          </>
        )}
      </div>

      <section className="border border-mesa-border p-6 space-y-4">
        <h2 className="text-sm font-semibold text-mesa-muted uppercase tracking-wide">Target details</h2>
        <dl className="grid grid-cols-[160px_1fr] gap-y-3 gap-x-4 text-sm">
          <dt className="text-mesa-muted">Target ID</dt>
          <dd className="font-mono text-xs flex items-center gap-2">
            <span>{webhook.id}</span>
            <button
              type="button"
              onClick={() => handleCopy(webhook.id)}
              className="text-mesa-muted hover:text-foreground"
              aria-label="Copy ID"
            >
              <Icon name="copy" className="h-3 w-3" />
            </button>
          </dd>

          <dt className="text-mesa-muted">Name</dt>
          <dd>{webhook.name ?? <span className="text-mesa-muted italic">— unset</span>}</dd>

          <dt className="text-mesa-muted">Endpoint URL</dt>
          <dd className="font-mono text-xs break-all">{webhook.url}</dd>

          <dt className="text-mesa-muted">Listening to</dt>
          <dd className="flex flex-wrap gap-1">
            {webhook.events.map((event) => (
              <Badge key={event} variant="secondary" className="font-mono text-xs">
                {event}
              </Badge>
            ))}
          </dd>

          <dt className="text-mesa-muted">Repos</dt>
          <dd>
            {webhook.repos === null ? (
              'All repos in this org'
            ) : (
              <span>
                {webhook.repos.length} {webhook.repos.length === 1 ? 'repo' : 'repos'}
              </span>
            )}
          </dd>

          <dt className="text-mesa-muted">Last triggered</dt>
          <dd>{formatDate(webhook.lastTriggeredAt)}</dd>

          <dt className="text-mesa-muted">Created</dt>
          <dd>{formatDate(webhook.createdAt)}</dd>
        </dl>
      </section>

      <section className="border border-mesa-border p-6 space-y-4">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-mesa-muted uppercase tracking-wide flex items-center gap-2">
            <Icon name="key" className="h-3.5 w-3.5" />
            Signing secret
          </h2>
          <p className="text-sm text-mesa-muted">Use this secret to verify webhook signatures in your receiver.</p>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-xs bg-mesa-bg-subtle px-3 py-2 border border-mesa-border break-all">
            {secretRevealed ? webhook.secret : '•'.repeat(Math.max(20, webhook.secret.length))}
          </code>
          <Button variant="outline" size="sm" onClick={() => setSecretRevealed((v) => !v)}>
            {secretRevealed ? 'Hide' : 'Show'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopySecret}>
            <Icon name={secretCopied ? 'check' : 'copy'} className="h-4 w-4 mr-1" />
            {secretCopied ? 'Copied' : 'Copy'}
          </Button>
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsRegenerateOpen(true)}
              className="text-mesa-red hover:text-mesa-red"
            >
              <Icon name="arrow-rotate-right" className="h-4 w-4 mr-1" />
              Regenerate
            </Button>
          )}
        </div>
      </section>

      <section className="border border-mesa-border p-6 space-y-3">
        <h2 className="text-sm font-semibold text-mesa-muted uppercase tracking-wide">Resources</h2>
        <ul className="space-y-2 text-sm">
          <li>
            <a
              href="https://docs.mesa.dev/content/getting-started/webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-mesa-blue hover:underline"
            >
              Webhook quickstart →
            </a>
          </li>
          <li>
            <a
              href="https://docs.mesa.dev/content/getting-started/webhooks#verify-signatures"
              target="_blank"
              rel="noopener noreferrer"
              className="text-mesa-blue hover:underline"
            >
              Verify a signature →
            </a>
          </li>
        </ul>
      </section>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this target?</AlertDialogTitle>
            <AlertDialogDescription>
              The target at <code className="font-mono text-xs">{webhook.url}</code> will stop receiving events
              immediately. Delivery history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete target'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isRegenerateOpen} onOpenChange={setIsRegenerateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate signing secret?</AlertDialogTitle>
            <AlertDialogDescription>
              The current secret will stop working immediately. Webhook deliveries signed with the new secret will fail
              signature verification at your receiver until you update it with the new value.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerateMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => regenerateMutation.mutate()}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending && <Icon name="spinner" solid spin className="size-4 mr-2" />}
              Regenerate secret
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WebhookEditDialog open={isEditOpen} onOpenChange={setIsEditOpen} orgSlug={org.slug} webhook={webhook} />
    </div>
  );
}
